import { createAppKit } from '@reown/appkit/react';
import { WagmiAdapter } from '@reown/appkit-adapter-wagmi';

import {
    cronosNetwork,
    CRONOS_CAIP_NETWORK_ID,
    DEFAULT_CRONOS_RPC_URLS,
    DEFAULT_ROBINHOOD_RPC_URLS,
    ROBINHOOD_CAIP_NETWORK_ID,
    supportedWalletNetworks,
} from './networks';

const DEFAULT_REOWN_PROJECT_ID = '772dd7be046dd3f98612677a3e538be6';

const projectId =
    import.meta.env.VITE_REOWN_PROJECT_ID ?? DEFAULT_REOWN_PROJECT_ID;
const preferredCronosRpcUrl = import.meta.env.VITE_CRONOS_RPC_URL?.trim();

const appMetadata = {
    name: 'SnappyTrade',
    description: 'SnappyTrade - perps in one click',
    url:
        typeof window !== 'undefined'
            ? window.location.origin
            : 'https://inout.app',
    icons: ['https://avatars.githubusercontent.com/u/179229932'],
};

const cronosRpcUrls = [
    preferredCronosRpcUrl,
    ...DEFAULT_CRONOS_RPC_URLS,
].filter((url): url is string => Boolean(url));

const uniqueCronosRpcUrls = Array.from(new Set(cronosRpcUrls));

// no env override here on purpose: the anchor's RPC decides where a signature's
// chain is read from, so it stays in code like the manager addresses do.
const robinhoodRpcUrls = Array.from(new Set(DEFAULT_ROBINHOOD_RPC_URLS));

const customRpcUrls = {
    [CRONOS_CAIP_NETWORK_ID]: uniqueCronosRpcUrls.map((url) => ({ url })),
    [ROBINHOOD_CAIP_NETWORK_ID]: robinhoodRpcUrls.map((url) => ({ url })),
};

/**
 * Why every line below is wrapped: this module used to construct the wagmi
 * adapter and call `createAppKit()` at import time, unguarded. An import that
 * throws takes the whole module graph with it — React never mounts, no error
 * boundary can catch it (they only catch RENDER errors, and there is no render),
 * and the user gets a black screen with the page title set.
 *
 * That is not hypothetical: wallet init is exactly what fails in an in-app
 * browser, where storage can be blocked, `indexedDB` absent, and the WalletConnect
 * transport unavailable. A user opening the app from a wallet's or a social app's
 * built-in browser hit precisely this.
 *
 * So: initialisation is allowed to fail. The app mounts either way, the wallet
 * surfaces degrade with a real message, and demo mode still works.
 */
let walletInitError: Error | null = null;

function asError(cause: unknown): Error {
    return cause instanceof Error ? cause : new Error(String(cause));
}

const wagmiAdapter = (() => {
    try {
        return new WagmiAdapter({
            projectId,
            networks: [...supportedWalletNetworks],
            customRpcUrls,
            ssr: false,
        });
    } catch (error) {
        walletInitError = asError(error);
        console.error('[wallet] adapter construction failed', error);
        return null;
    }
})();

let isAppKitInitialized = false;

export function initializeAppKit(): void {
    if (isAppKitInitialized || wagmiAdapter === null) {
        return;
    }

    createAppKit({
        adapters: [wagmiAdapter],
        networks: [...supportedWalletNetworks],
        defaultNetwork: cronosNetwork,
        allowUnsupportedChain: true,
        projectId,
        metadata: appMetadata,
        customRpcUrls,
        defaultAccountTypes: {
            eip155: 'eoa',
        },
        features: {
            analytics: false,
        },
    });

    isAppKitInitialized = true;
}

try {
    initializeAppKit();
} catch (error) {
    walletInitError ??= asError(error);
    console.error('[wallet] AppKit initialisation failed', error);
}

/**
 * Stands in for the wagmi config when the adapter could not be built.
 *
 * A null config would ripple `?.` through ~20 call sites and, worse, let a wallet
 * action quietly no-op. Every call site here is behind a user action, so throwing
 * ON USE is both safe and honest: the app runs, and the flow that genuinely needs
 * a wallet fails with a sentence a human can act on instead of undefined.
 */
function unavailableWagmiConfig(): never {
    throw new Error(
        walletInitError
            ? `Wallet support is unavailable in this browser: ${walletInitError.message}`
            : 'Wallet support is unavailable in this browser.',
    );
}

export const walletWagmiConfig = (wagmiAdapter?.wagmiConfig ??
    new Proxy({} as Record<string, never>, {
        get: unavailableWagmiConfig,
        apply: unavailableWagmiConfig,
    })) as NonNullable<typeof wagmiAdapter>['wagmiConfig'];

/** Non-null when wallet init failed — the UI uses it to explain, not to crash. */
export function getWalletInitError(): Error | null {
    return walletInitError;
}

export const reownProjectId = projectId;
